import { useState, useEffect } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Grid,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SbuxDialog2 from "../../../../components/SbuxDialog2";
import SbuxTranslate from "../../../../components/SbuxTranslate";

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (start: Date | null, end: Date | null) => void;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  includeTime?: boolean;
}

const DateRangeFilterDialog = ({
  open,
  onClose,
  onApply,
  initialStartDate = null,
  initialEndDate = null,
  includeTime = false,
}: Props) => {
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate);
  const [startTime, setStartTime] = useState<Date | null>(initialStartDate);
  const [endTime, setEndTime] = useState<Date | null>(initialEndDate);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [error, setError] = useState("");

  const combineDateAndTime = (
    date: Date | null,
    time: Date | null,
  ): Date | null => {
    if (!date || !time) return null;
    const combined = new Date(date);
    combined.setHours(time.getHours());
    combined.setMinutes(time.getMinutes());
    return combined;
  };

  const validateDates = () => {
    setError("");
    if (!startDate || !endDate) return;
    if (includeTime && (!startTime || !endTime)) return;

    const finalStart = includeTime
      ? combineDateAndTime(startDate, startTime)
      : startDate;
    const finalEnd = includeTime
      ? combineDateAndTime(endDate, endTime)
      : endDate;

    if (finalStart && finalEnd && finalStart.getTime() > finalEnd.getTime()) {
      setError("Start date must be before or equal to end date.");
    }
  };

  useEffect(() => {
    if (hasInteracted) {
      validateDates();
    }
  }, [startDate, endDate, startTime, endTime]);

  const handleApply = () => {
    const finalStart = includeTime
      ? combineDateAndTime(startDate, startTime)
      : startDate;
    const finalEnd = includeTime
      ? combineDateAndTime(endDate, endTime)
      : endDate;

    onApply(finalStart, finalEnd);
    onClose();
  };

  const dialogConfig = {
    title: <SbuxTranslate>{"Select Date Range"}</SbuxTranslate>,
    subtitle: "",
  };

  return (
    <SbuxDialog2 open={open} config={dialogConfig} handleOnClose={onClose}>
      <DialogContent dividers sx={{ height: "300px", overflowY: "auto" }}>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => {
                  setStartDate(date);
                  setHasInteracted(true);
                }}
                dateFormat="MM/dd/yyyy"
                popperPlacement="right-start"
                customInput={<TextField label="Start Date" fullWidth />}
              />
            </Grid>
            {includeTime && (
              <Grid item xs={6}>
                <DatePicker
                  selected={startTime}
                  onChange={(time: Date | null) => {
                    setStartTime(time);
                    setHasInteracted(true);
                  }}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Start Time"
                  dateFormat="hh:mm aa"
                  popperPlacement="right-start"
                  customInput={<TextField label="Start Time" fullWidth />}
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  setEndDate(date);
                  setHasInteracted(true);
                }}
                dateFormat="MM/dd/yyyy"
                popperPlacement="right-start"
                customInput={
                  <TextField
                    label="End Date"
                    fullWidth
                    error={!!error}
                    helperText={error}
                    FormHelperTextProps={{
                      sx: {
                        marginTop: 0,
                        marginLeft: 0,
                      },
                    }}
                  />
                }
              />
            </Grid>
            {includeTime && (
              <Grid item xs={6}>
                <DatePicker
                  selected={endTime}
                  onChange={(time: Date | null) => {
                    setEndTime(time);
                    setHasInteracted(true);
                  }}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="End Time"
                  dateFormat="hh:mm aa"
                  popperPlacement="right-start"
                  customInput={<TextField label="End Time" fullWidth />}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box className="actionContainer">
          <Button
            onClick={handleApply}
            variant="contained"
            size="medium"
            disabled={
              !startDate ||
              !endDate ||
              (includeTime && (!startTime || !endTime)) ||
              !!error
            }
          >
            <SbuxTranslate>{"Apply"}</SbuxTranslate>
          </Button>
          <Button onClick={onClose} variant="outlined" size="medium">
            <SbuxTranslate>{"Cancel"}</SbuxTranslate>
          </Button>
        </Box>
      </DialogActions>
    </SbuxDialog2>
  );
};

export default DateRangeFilterDialog;
